import React, { useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import FieldGroup from '../FieldGroup';
import SectionTitle from '../SectionTitle';
import Dropdown from '../Dropdown';
import UploadField from '../UploadField';
import Separator from '../Separator';

const languageOptions = [
  { value: 'en-AU', label: 'English (Australian)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-UK', label: 'English (UK)' },
  { value: 'fil-PH', label: 'Filipino / Tagalog' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'ar-SA', label: 'Arabic (RTL)' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'other', label: 'Other' },
];

const rtlLanguages = ['ar-SA', 'ps-AF'];

const slugify = (text) =>
  text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');

function Step1Identity() {
  const { projectDraft, dispatch } = useAppContext();
  const { siteTitle = '', primaryLanguage = 'en-AU' } = projectDraft;

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { [name]: value } });
  };

  const handleFileChange = (file, type) => {
    if (!file) return;
    console.log(`File selected for ${type}:`, file.name);
    // Simulate file upload
    const assetKey = `${type}Path`;
    dispatch({
      type: 'UPDATE_PROJECT_DRAFT',
      payload: { 
        assets: { 
          ...projectDraft.assets, 
          [assetKey]: `/assets/${file.name}` 
        } 
      },
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { [name]: checked } });
  };

  useEffect(() => {
    if (siteTitle) {
      dispatch({ type: 'UPDATE_PROJECT_DRAFT', payload: { siteSlug: slugify(siteTitle) } });
    }
  }, [siteTitle, dispatch]);

  useEffect(() => {
    let spellingVariant = 'N/A';
    if (primaryLanguage.startsWith('en-AU') || primaryLanguage.startsWith('en-UK')) {
      spellingVariant = 'AU/UK';
    } else if (primaryLanguage.startsWith('en-US')) {
      spellingVariant = 'US';
    }
    const isRtl = rtlLanguages.includes(primaryLanguage);
    dispatch({
      type: 'UPDATE_PROJECT_DRAFT',
      payload: { spellingVariant, rtlLayout: isRtl },
    });
  }, [primaryLanguage, dispatch]);

  return (
    <div className="space-y-6">
      <SectionTitle title="Project Identity" subtitle="Define the core identity of your project." />
      
      <FieldGroup label="Developer Name">
        <input
          type="text"
          name="developerName"
          value={projectDraft.developerName || ''}
          onChange={handleChange}
          className="form-input"
          placeholder="Your name"
        />
      </FieldGroup>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Site Title">
          <input
            type="text"
            name="siteTitle"
            value={projectDraft.siteTitle || ''}
            onChange={handleChange}
            className="form-input"
            placeholder="My Awesome Project"
          />
        </FieldGroup>
        
        <FieldGroup label="Site Slug" description="Auto-generated from site title">
          <input
            type="text"
            name="siteSlug"
            value={projectDraft.siteSlug || ''}
            readOnly
            className="form-input bg-gray-50"
          />
        </FieldGroup>
      </div>

      <Separator />

      <SectionTitle title="Project Assets" subtitle="Upload your branding assets." />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <UploadField 
          label="Logo" 
          onFileSelect={file => handleFileChange(file, 'logo')} 
        />
        <UploadField 
          label="Favicon" 
          onFileSelect={file => handleFileChange(file, 'favicon')} 
        />
        <UploadField 
          label="Touch Icon" 
          onFileSelect={file => handleFileChange(file, 'touchIcon')} 
        />
      </div>

      <Separator />

      <SectionTitle title="Language & Locale" subtitle="Configure language and regional settings." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Primary Language">
          <Dropdown 
            options={languageOptions} 
            name="primaryLanguage" 
            value={projectDraft.primaryLanguage} 
            onChange={handleChange} 
          />
        </FieldGroup>
        
        <FieldGroup label="Spelling Variant" description="Auto-detected from language">
          <input
            type="text"
            value={projectDraft.spellingVariant || 'N/A'}
            readOnly
            className="form-input bg-gray-50"
          />
        </FieldGroup>
      </div>
      
      <div className="flex items-center">
        <input 
          type="checkbox" 
          name="rtlLayout" 
          id="rtlLayout" 
          checked={projectDraft.rtlLayout || false} 
          onChange={handleCheckboxChange} 
          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
        />
        <label htmlFor="rtlLayout" className="ml-2 block text-sm text-gray-900">
          Enable RTL Layout
        </label>
      </div>
    </div>
  );
}

export default Step1Identity;